import React from 'react';
import {Box} from '@mui/material';
import {
  Button,
  Body,
  designSystemToken,
  Icon,
} from '@lightricks/react-design-system';
import downloadAuthenticatedCsv from '@/utils/downloadAuthenticatedCsv';
import {
  generateSharedListUrl,
  SharedListUrlOptions,
} from '@/utils/generateSharedListUrl';
import translate from '@/utils/translate';
import CreatorGroupLabel from '@/types/creatorGroupLabel';
import usePreferredCreatorGroupMemberships from '@/views/creators/lists/hooks/usePreferredCreatorGroupMemberships';
import CheckboxLabel from '@/components/checkbox-label';
import useCopyToClipboardWithFeedback from '@/hooks/useCopyToClipboardWithFeedback';
import styles from './ShareList.module.scss';

const TRANSLATION_PREFIX = 'components.share-list';

const LINK_COPIED_RESET_MS = 3000;

type ShareListProps = {
  label?: CreatorGroupLabel;
  refId?: string;
  type?: SharedListUrlOptions['type'];
};

function ShareList({label, refId, type}: ShareListProps) {
  const {creatorGroupId} = usePreferredCreatorGroupMemberships();
  const [posts, setPosts] = React.useState(true);
  const [networkAnalytics, setNetworkAnalytics] = React.useState(true);
  const [portfolio, setPortfolio] = React.useState(true);
  const {copied: linkCopied, copyToClipboard} =
    useCopyToClipboardWithFeedback(LINK_COPIED_RESET_MS);

  const resolvedRefId = refId ?? label?.id ?? creatorGroupId;
  const resolvedType: SharedListUrlOptions['type'] =
    type ?? (label?.id ? 'labels' : 'creator_groups');

  const downloadCSV = () => {
    downloadAuthenticatedCsv(resolvedType, resolvedRefId);
  };

  const copyLink = () => {
    const shareableUrl = generateSharedListUrl({
      refId: resolvedRefId,
      type: resolvedType,
      includePosts: posts,
      includeNetworkAnalytics: networkAnalytics,
      includePortfolio: portfolio,
    });
    copyToClipboard(shareableUrl);
  };

  return (
    <div className={styles.content}>
      <Box className={styles.checkboxContainer}>
        <Body size="lg" color={designSystemToken('semantic.fg.secondary')}>
          {translate(`${TRANSLATION_PREFIX}.description`)}
        </Body>
        <CheckboxLabel
          checked={portfolio}
          onChange={() => {
            setPortfolio(!portfolio);
          }}
          label={translate(`${TRANSLATION_PREFIX}.options.overview`)}
        />
        <CheckboxLabel
          checked={networkAnalytics}
          onChange={() => {
            setNetworkAnalytics(!networkAnalytics);
          }}
          label={translate(`${TRANSLATION_PREFIX}.options.analytics`)}
        />
        <CheckboxLabel
          checked={posts}
          onChange={() => {
            setPosts(!posts);
          }}
          label={translate(`${TRANSLATION_PREFIX}.options.posts`)}
        />
      </Box>
      <div className={styles.saveButtonContainer}>
        <Button
          appearance="neutral"
          size="large"
          mode="tinted"
          className={styles.saveButton}
          onClick={downloadCSV}
          icon={
            <Icon
              name="Actions-Save-Single"
              size="medium"
              appearance="neutral"
            />
          }
        >
          {translate(`${TRANSLATION_PREFIX}.download`)}
        </Button>
        <Button
          appearance="neutral"
          size="large"
          mode="filled"
          className={styles.copyButton}
          onClick={copyLink}
          style={{
            backgroundColor: linkCopied
              ? designSystemToken('semantic.bg.neutral')
              : '',
          }}
          icon={
            !linkCopied ? (
              <Icon name="Navigation-Copy" size="medium" appearance="inverse" />
            ) : null
          }
        >
          {linkCopied
            ? translate(`${TRANSLATION_PREFIX}.copied`)
            : translate(`${TRANSLATION_PREFIX}.copy`)}
        </Button>
      </div>
    </div>
  );
}

ShareList.textAlignment = 'left' as const;
ShareList.footerAlignment = 'right' as const;

export default ShareList;
